import {
    BooleanFilter,
    CheckboxFilter,
    DateFilter,
    FilterTemplate,
    GroupMultiSelectFilter,
    MultiSelectFilter,
    NumberFilter,
    RadioFilter,
    SelectFilter,
    StringFilter,
    TimeFilter,
    GroupSelectFilter,
} from '@models/filter-types';
import {
    BooleanLookupItem,
    ChannelItem,
    DaypartLookupItem,
    LookupItem,
    MarketItem,
    MonthYear,
    Period,
    MetricsData,
    PricingTier,
    RateCardPeriod,
    SavedReport,
    StationItem,
    PriorityCodeTiersQuarter,
    SavedReportInfoFromPopUp,
    ChannelGroupItem,
    SavedReportSomething,
    SegmentItem,
    EditReportConfig,
} from '@models/lookup';
import { dateFormatter, isSingleOptionSelected } from '@shared/helpers/functions/helpers';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '@services/notification/notification.service';
import { loadSavedValuesIntoFiltersAsync } from '@shared/helpers/functions/filter-helpers';
import { forkJoin, of } from 'rxjs';
import { LoadReportPopupComponent } from '@shared/components/report-components/load-report-popup/load-report-popup.component';
import { SaveReportPopupComponent } from '@shared/components/report-components/save-report-popup/save-report-popup.component';
import { FilterOption } from './rate-card/rate-card';
import { UrlStore } from '@shared/helpers/constants/url-store';
import moment from 'moment';
import { AdvertiserInsightsDependentFiltersResponse } from '@models/advertiser-insights/advertiser-insights';
import { AdvertiserInsightsFilters } from '@models/advertiser-insights/advertiser-insights-filters';
import { FilterSettings } from '@models/filter';
import { UiConfig } from '@models/config';
import { AdminService } from '@services/admin/admin.service';
import { defaultIfEmpty } from 'rxjs/operators';
import { DynamicRateCardFiltersV2 } from '@models/rate-card/dynamic-rate-card-filters-v2';
import { PoliticalRateCardFiltersV2 } from '@models/rate-card/political-rate-card-filters-v2';
import { DigitalRateCardFilters } from '@models/rate-card/digital-rate-card-filters';
import { CONFIRM_DIALOG, ConfirmDialogComponent, DENY_DIALOG } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { THROUGH_QUARTER_END_OPTION } from '@shared/helpers/functions/rolling-dates';

export function applyPrimaryMarketDefault(
    marketsFilter: MultiSelectFilter<MarketItem>,
    config: UiConfig | undefined,
    items: MarketItem[],
): boolean {
    if (marketsFilter.Value.length > 0 || !config?.primaryMarket) return false;
    const match = items.find((i) => i.id === config.primaryMarket);
    if (match) {
        marketsFilter.Default([match]);
        return true;
    }
    return false;
}

export const channelGroupFilter: (settings: FilterSettings) => SelectFilter<ChannelGroupItem> = (settings: FilterSettings) => {
    const filter: SelectFilter<ChannelGroupItem> = new SelectFilter<ChannelGroupItem>(
        settings.config.customerCapability.rate_optics.general.station
            ? (settings.config.customerConfig.station || 'Station') + ' Group'
            : 'Channel Group',
    )
        .Defer(() =>
            settings.lookupSvc.getChannelGroups(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((item: LookupItem) => item?.id)
        .Required(true)
        .IsClearable(false)
        .Clear(() => (filter.Value = null))
        .Icon('fas fa-object-group');
    return filter;
};

export const channelGroupsFilter: (settings: FilterSettings) => MultiSelectFilter<ChannelGroupItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<ChannelGroupItem> = new MultiSelectFilter<ChannelGroupItem>(
        settings.config.customerCapability.rate_optics.general.station
            ? (settings.config.customerConfig.station || 'Station') + ' Groups'
            : 'Channel Groups',
    )
        .Defer(() =>
            settings.lookupSvc.getChannelGroups(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((groups) => groups.map((group) => group.id))
        .Required(true)
        .IsClearable(false)
        .Clear(() => (filter.Value = []))
        .Icon('fas fa-object-group');
    return filter;
};

export const marketsFilter: (settings: FilterSettings) => MultiSelectFilter<MarketItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<MarketItem> = new MultiSelectFilter<MarketItem>('Markets')
        .Defer(() =>
            settings.lookupSvc.getMarkets(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((items: MarketItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config?.roleCapability?.rate_optics.general.markets)
        .Icon('fas fa-map-marked-alt');
    return filter;
};

export const stationsFilter: (settings: FilterSettings) => GroupMultiSelectFilter<StationItem> = (settings: FilterSettings) => {
    const filter: GroupMultiSelectFilter<StationItem> = new GroupMultiSelectFilter<StationItem>(
        (settings.config.customerConfig.station || 'Station') + 's',
    )
        .Defer(() =>
            settings.lookupSvc.getStations(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((items: StationItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config.roleCapability.rate_optics.general.station)
        .Icon('fas fa-tower-cell');
    return filter;
};

export const channelsFilterSingleSelect: (settings: FilterSettings) => SelectFilter<ChannelItem> = (settings: FilterSettings) => {
    const filter: SelectFilter<ChannelItem> = new SelectFilter<ChannelItem>((settings.config.customerConfig.channel as string) || 'Channel')
        .Defer(() =>
            settings.lookupSvc.getChannels(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((item: ChannelItem) => item.id)
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config.roleCapability.rate_optics.general.channel)
        .Icon('fas fa-network-wired');
    return filter;
};

export const pCodeMarketsFilter: (settings: FilterSettings) => MultiSelectFilter<MarketItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<MarketItem> = new MultiSelectFilter<MarketItem>('Markets')
        .Defer(() => settings.lookupSvc.getPCodeMarkets(false, settings.hasDynamicRates))
        .Transform((items: MarketItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config?.roleCapability?.rate_optics.general.markets)
        .Icon('fas fa-map-marked-alt');
    return filter;
};

export const pCodeStationsFilter: (settings: FilterSettings) => GroupMultiSelectFilter<StationItem> = (settings: FilterSettings) => {
    const filter: GroupMultiSelectFilter<StationItem> = new GroupMultiSelectFilter<StationItem>('Stations')
        .Defer(() => settings.lookupSvc.getPCodeStations(false, settings.hasDynamicRates))
        .Transform((items: StationItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config?.roleCapability?.rate_optics.general.station)
        .Icon('fas fa-map-marked-alt');
    return filter;
};

export const pCodeChannelsFilter: (settings: FilterSettings) => MultiSelectFilter<ChannelItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<ChannelItem> = new MultiSelectFilter<ChannelItem>('Channels')
        .Defer(() => settings.lookupSvc.getPCodeChannels(false, settings.hasDynamicRates))
        .Filterable(true)
        .Transform((items: ChannelItem[]) => items.map((item) => item?.id))
        .Required(true)
        .Toggle(!settings.config.roleCapability.rate_optics.general.channel)
        .Icon('fas fa-network-wired');
    return filter;
};

export const stationsFilterSingleSelect: (settings: FilterSettings) => SelectFilter<StationItem> = (settings: FilterSettings) => {
    const filter: SelectFilter<StationItem> = new SelectFilter<StationItem>((settings.config.customerConfig.station || 'Station') + 's')
        .Defer(() =>
            settings.lookupSvc.getStations(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((item: StationItem) => item.id)
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config.roleCapability.rate_optics.general.station)
        .Icon('fas fa-tower-cell');
    return filter;
};

/** single-select stations with the dropdown grouped by market — the
 * grouped single-select sibling of stationsFilter (grouped multi, used by
 * the rate card) and stationsFilterSingleSelect (flat single) */
export const stationsFilterGroupSingleSelect: (settings: FilterSettings) => GroupSelectFilter<StationItem> = (settings: FilterSettings) => {
    const filter: GroupSelectFilter<StationItem> = new GroupSelectFilter<StationItem>(
        ((settings.config.customerConfig.station as string) || 'Station') + 's',
    )
        .Defer(() =>
            settings.lookupSvc.getStations(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
            ),
        )
        .Transform((item: StationItem) => item.id)
        .Filterable(true)
        .Required(true)
        .Toggle(!settings.config.roleCapability.rate_optics.general.station)
        .Icon('fas fa-tower-cell');
    return filter;
};

export const quarterFilterSingleSelect: () => SelectFilter<MonthYear> = () => {
    const filter: SelectFilter<MonthYear> = new SelectFilter<MonthYear>('Quarter')
        .Transform((item: MonthYear) => item?.id)
        .Filterable(true)
        .OnLoad((items: MonthYear[]) => {
            const today = new Date();
            const quarterNumber = Math.ceil((today.getMonth() + 1) / 3); //Months starts at 0 for January
            const year = today.getFullYear();
            const quarterName = 'Quarter ' + quarterNumber.toString() + ' ' + year.toString();
            if (items.length > 0) {
                filter.Value = items.find((item) => item.name === quarterName);
            }
        })
        .Required(true)
        .Icon('fas fa-calendar-alt');
    return filter;
};

export const channelsFilter: (settings: FilterSettings) => MultiSelectFilter<ChannelItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<ChannelItem> = new MultiSelectFilter<ChannelItem>('Channels')
        .Defer(() =>
            settings.lookupSvc.getChannels(
                false,
                settings.hasDynamicRates,
                settings.hasPoliticalRates,
                settings.hasDigitalRates,
                settings.tabNumber,
                settings.hasDashboardData,
            ),
        )
        .Filterable(true)
        .Transform((items: ChannelItem[]) => items.map((item) => item.id))
        .Toggle(!settings.config?.roleCapability?.rate_optics?.general?.channel)
        .Required(true)
        .Icon('fas fa-network-wired');
    return filter;
};

export const periodFilter: (settings: FilterSettings) => SelectFilter<Period> = (settings: FilterSettings) => {
    const filter: SelectFilter<Period> = new SelectFilter<Period>('Period')
        .Defer(() => settings.lookupSvc.getPeriods(settings.screenName, settings.tabNumber, null))
        .Transform((v) => ({ id: v?.id, name: v?.name }))
        .Filterable(true)
        .RestoreWithId(false)
        .Clear(
            () =>
                (filter.Value =
                    filter.options.find((option) => option.isDefault) ||
                    filter.options.find((option) => option.name === 'Week') ||
                    filter.options[0]),
        )
        .Icon('fas fa-chart-column');
    return filter;
};

export const pcodeQuarterFilter: (settings: FilterSettings) => MultiSelectFilter<PriorityCodeTiersQuarter> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<PriorityCodeTiersQuarter> = new MultiSelectFilter<PriorityCodeTiersQuarter>('Quarter')
        .Transform((items) => items.map((item) => item.name))
        .Icon('fas fa-chart-column')
        .EmptySearchText('Load data to see quarter options')
        .Filterable(true);
    return filter;
};

export const startDateFilter: () => DateFilter = () => {
    const filter: DateFilter = new DateFilter('Start Date').Icon('fas fa-calendar-plus').View('Week');
    return filter;
};

export const endDateFilter: () => DateFilter = () => {
    const filter: DateFilter = new DateFilter('End Date').Icon('fas fa-calendar-minus').View('Week').IsEndDate(true);
    return filter;
};

export const startTimeFilter: (key?: string) => TimeFilter = (key = 'Start Time') => {
    const filter: TimeFilter = new TimeFilter(key).Icon('fas fa-clock-plus');
    return filter;
};

export const endTimeFilter: (key?: string) => TimeFilter = (key = 'End Time') => {
    const filter: TimeFilter = new TimeFilter(key).Icon('fas fa-clock-plus');
    return filter;
};

export const rateFilter: (min: number, max: number, spotLength: number, tier: string) => NumberFilter = (min, max, spotLength, tier) => {
    const filter: NumberFilter = new NumberFilter(`Rate (${spotLength ? spotLength + 's' : ''} ${tier || ''})`).Min(min).Max(max);
    return filter;
};

export const daypartsFilter: (settings: FilterSettings) => MultiSelectFilter<DaypartLookupItem> = (settings: FilterSettings) => {
    const filter: MultiSelectFilter<DaypartLookupItem> = new MultiSelectFilter<DaypartLookupItem>('Dayparts')
        .CompareFn((l, r) => isSingleOptionSelected(l, r))
        .Transform((items) => items.map((daypart) => daypart.id))
        .Icon('fas fa-calendar-day')
        .Filterable(true)
        .Toggle(false)
        .Required(true);
    return filter;
};

export const daypartsSingleFilter: (settings: FilterSettings) => SelectFilter<DaypartLookupItem> = (settings: FilterSettings) => {
    const filter: SelectFilter<DaypartLookupItem> = new SelectFilter<DaypartLookupItem>('Daypart')
        .CompareFn((l, r) => isSingleOptionSelected(l, r))
        .Transform((item) => item.id)
        .Icon('fas fa-calendar-day')
        .Filterable(true)
        .Toggle(false)
        .Required(true);
    return filter;
};

export const spotLengthsFilter: () => MultiSelectFilter<LookupItem> = () => {
    const filter: MultiSelectFilter<LookupItem> = new MultiSelectFilter<LookupItem>('Spot Lengths')
        .Transform((spotLengths) => spotLengths.map((spotLength) => spotLength.id))
        .Icon('fas fa-stopwatch')
        .Required(true);
    return filter;
};

export const spotTypesFilter: () => MultiSelectFilter<LookupItem> = () => {
    const filter: MultiSelectFilter<LookupItem> = new MultiSelectFilter<LookupItem>('Spot Type')
        .Icon('fas fa-bullseye')
        .Filterable(true)
        .Transform((spotTypes) => spotTypes.map((spotType) => spotType.id))
        .Required(true);
    return filter;
};

export const dowsFilter: () => CheckboxFilter<BooleanLookupItem> = () => {
    const filter: CheckboxFilter<BooleanLookupItem> = new CheckboxFilter<BooleanLookupItem>('DOWs')
        .Required(true)
        .DisplayKey(false)
        .Transform((_) => filter.options.filter((dow) => dow.included).map((dow) => dow.id));
    return filter;
};

export const pricingTiersFilter: () => MultiSelectFilter<PricingTier> = () => {
    const filter: MultiSelectFilter<PricingTier> = new MultiSelectFilter<PricingTier>('Pricing Tiers')
        .CompareFn((l, r) => l.id === r.id)
        .Transform((v) => v.map((val) => val.id))
        .Icon('fas fa-gift')
        .Filterable(true)
        .Clear(() => (filter.Value = filter.options));
    return filter;
};

export const politicalTiersFilter: () => MultiSelectFilter<LookupItem> = () => {
    const filter: MultiSelectFilter<LookupItem> = new MultiSelectFilter<LookupItem>('Political Tiers')
        .CompareFn((l, r) => l.id === r.id)
        .Transform((v) => v.map((val) => val.id))
        .Icon('fas fa-gift')
        .Filterable(true)
        .Clear(() => (filter.Value = filter.options));
    return filter;
};

export const flightWeeksFilter: (resetFlightWeeks: () => void) => MultiSelectFilter<string> = (resetFlightWeeks: () => void) => {
    const filter: MultiSelectFilter<string> = new MultiSelectFilter<string>('Flight Weeks')
        .Defer(() => of([]))
        .OnLoad((items: string[]) => {
            resetFlightWeeks();
        })
        .Clear(() => (filter.Value = filter.options))
        .TriggerLength(3)
        .Transform((v: string[]) => v.map((week) => dateFormatter(week, 'yyyy/MM/dd')))
        .RestoreWithId(false)
        .Icon('fas fa-paper-plane');
    return filter;
};

export const productsFilter: () => MultiSelectFilter<LookupItem> = () => {
    const filter: MultiSelectFilter<LookupItem> = new MultiSelectFilter<LookupItem>('Products')
        .Transform((products) => products.map((product) => product.id))
        .Required(true)
        .IsClearable(false)
        .Clear(() => (filter.Value = []))
        .Icon('fas fa-barcode');
    return filter;
};

export const productTypesFilter: () => MultiSelectFilter<LookupItem> = () => {
    const filter: MultiSelectFilter<LookupItem> = new MultiSelectFilter<LookupItem>('Product Types')
        .Transform((products) => products.map((product) => product.id))
        .Required(true)
        .IsClearable(false)
        .Clear(() => (filter.Value = []))
        .Icon('fas fa-barcode');
    return filter;
};

export const moduleNamesFilter: (adminSvc: AdminService, screenName: string) => SelectFilter<LookupItem> = (
    adminSvc: AdminService,
    screenName: string,
) => {
    const filter: SelectFilter<LookupItem> = new SelectFilter<LookupItem>('Module')
        .Defer(() => adminSvc.getModules(screenName))
        .Transform((item: LookupItem) => item?.id)
        .Required(false)
        .IsClearable(false)
        .Clear(() => (filter.Value = null))
        .Icon('fas fa-object-group');
    return filter;
};

export interface ReportData {
    savedMetrics: string[];
    savedSegments: string[];
    savedDemos?: string[];
    startDate: string;
    endDate: string;
    global: boolean;
    favorited: boolean;
    useRollingDates: boolean;
    rollingThroughQuarterEnd?: boolean;
    rollingPeriod: string;
    rollingPeriodStart: number;
    rollingPeriodNumber: number;
    metricsOrder: string[];
    segmentsOrder: string[];
    savedDemosOrder?: string[];
    stationOrder?: number[];
    marketOrder?: number[];
    reportName?: string;
}

const savedDateRangeOptionName = (editReportConfig?: EditReportConfig): string => {
    if (!editReportConfig?.isEditMode || !editReportConfig.report.isRollingDates) {
        return 'Save Fixed Dates';
    }
    return editReportConfig.report.filter.rolling_through_quarter_end ? THROUGH_QUARTER_END_OPTION : 'Save Rolling Dates';
};

const saveMetricSelectionsToggle = (isEditMode: boolean): BooleanFilter =>
    isEditMode
        ? new BooleanFilter('Save current metric selections', false)
              .Slider(true)
              .Hint("Replaces this report's metrics, segments, demos and station order with your current selections")
        : new BooleanFilter('Save Metric Selections', true).Slider(true);

const keepsReportSettings = (editReportConfig: EditReportConfig, saveMetricSelectionsFilter: BooleanFilter): boolean =>
    !!editReportConfig?.isEditMode && (!editReportConfig.report.isReportOwner || !saveMetricSelectionsFilter.Value);

const canSaveMetricSelections = (editReportConfig?: EditReportConfig): boolean =>
    !!editReportConfig?.isEditMode && editReportConfig.report.isReportOwner;

export const saveReport = <T>(
    matDialog: MatDialog,
    url: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBarSvc: NotificationService,
    metricsData: MetricsData,
    segmentsKey: string,
    segmentsOrderKey: string,
    includeQuickDataSelect: boolean,
    includeDateRange: boolean,
    filterJSON: T,
    filtersCheck: boolean,
    tabData: number = 1,
    /* eslint-disable max-params */
    resetEndDate: boolean = true,
    editReportConfig?: EditReportConfig,
    demosKey: string = '',
    deemosOrderKey: string = '',
): void => {
    /* eslint-enable max-params */
    const isEditMode = editReportConfig?.isEditMode || false;
    let existingReports: SavedReport[] = [];

    lookupSvc.loadSavedReports(url, tabData).subscribe(
        (result: SavedReport[]) => {
            existingReports = result;
        },
        (error) => {
            console.error(error);
        },
    );

    const originalName = isEditMode ? editReportConfig.report.reportName : '';
    // @ts-expect-error TS7053: implicit any
    const nameFilter = new StringFilter('Report Name')
        .Validator(
            (val: string | null): boolean => {
                if (val === null) {
                    return false;
                }
                const upperVal = val.toUpperCase();
                if (isEditMode && upperVal === originalName.toUpperCase()) {
                    return true;
                }
                return existingReports.every((report) => report.reportName.toUpperCase() !== upperVal);
            },
            // @ts-expect-error TS7024: implicit any return type
            () => `Name Already Exists ${nameFilter.value}`,
        )
        .Required(true);

    const saveMetricSelectionsFilter = saveMetricSelectionsToggle(isEditMode);

    const saveAsGlobalFilter = new BooleanFilter(
        isEditMode ? 'Global' : 'Save as Global',
        isEditMode ? editReportConfig.report.isGlobal : false,
    )
        .Slider(true)
        .Toggle(!filter.config.roleCapability.rate_optics.general.create_global_report)
        .Hint('Make this report available to all users');

    if (isEditMode) {
        nameFilter.Default(originalName);
        saveAsGlobalFilter.Hint('Make this report available to all users');
        if (!editReportConfig.report.isReportOwner) {
            saveAsGlobalFilter.Readonly();
        }
    }
    const numberOfPeriodsFilter = new NumberFilter('Number of Periods').Min(1).Required(true);

    const periodSelectFilter = new SelectFilter<Period>('Period').OnChange((period: Period) => {
        const periodName = period?.name ? period.name + 's' : 'Periods';
        numberOfPeriodsFilter.Key = `Number of ${periodName}`;
    });
    lookupSvc.getPeriods(url).subscribe(
        (res) => {
            periodSelectFilter.Options(res);
            if (
                [UrlStore.screenNames.rateCard, UrlStore.screenNames.politicalRateCard, UrlStore.screenNames.digitalRateCard].includes(url)
            ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const reportFilter = filter as any;
                if (reportFilter.period) {
                    periodSelectFilter.Value = res.find((p) => p.name === reportFilter.period.value.name);
                    periodSelectFilter.hide = true;
                }
            }
            if (url === UrlStore.screenNames.politicalLur || url === UrlStore.screenNames.rateGenerator) {
                periodSelectFilter.Options(periodSelectFilter.options.filter((option) => option.name === 'Week'));
            }
            if (isEditMode && editReportConfig.report.isRollingDates) {
                const periodIndex = res.findIndex((p) => p.name === editReportConfig.report.filter.rolling_period);
                if (periodIndex !== -1) {
                    periodSelectFilter.Value = res[periodIndex];
                    numberOfPeriodsFilter.Value = editReportConfig.report.filter.rolling_period_number;
                }
            }
        },
        (error) => {
            console.error(error);
        },
    );

    const quickDateOptionsSelectFilter = new SelectFilter<LookupItem>('Quick Date Options').Options([
        { id: 1, name: 'Current Quarter', displayName: 'Current Quarter' },
        { id: 2, name: 'Next Quarter', displayName: 'Next Quarter' },
        { id: 3, name: 'Current Year', displayName: 'Current Year' },
        { id: 4, name: 'Next Year', displayName: 'Next Year' },
    ]);

    const radioOptions = [];

    radioOptions.push({
        name: 'Save Fixed Dates',
        filterFunc: () => true,
    });

    if (includeDateRange) {
        radioOptions.push({
            name: 'Save Rolling Dates',
            filterFunc: () => true,
        });
    }
    if (includeDateRange && url === UrlStore.screenNames.rateCard) {
        radioOptions.push({
            name: THROUGH_QUARTER_END_OPTION,
            filterFunc: () => true,
        });
    }
    //Hide QuickDate Select until the Quick Date options filter is ready to go to PROD
    if (includeQuickDataSelect) {
        // radioOptions.push({
        //     name: 'Quick Date Options',
        //     filterFunc: () => true
        // });
    }
    const dateRangeOptionsFilter = new RadioFilter<FilterOption>('').DisplayKey(false).Options(radioOptions);
    dateRangeOptionsFilter.Value =
        dateRangeOptionsFilter.options.find((option) => option.name === savedDateRangeOptionName(editReportConfig)) ??
        dateRangeOptionsFilter.options[0];
    const addAsFavoriteFilter = new BooleanFilter('Set as Favorite', false).Slider(true);

    let rateCardPeriods: RateCardPeriod[];
    lookupSvc.getRateCardPeriods(moment(new Date()).format('YYYY-MM-DD')).subscribe(
        (res) => {
            rateCardPeriods = res;
        },
        (error) => {
            console.error(error);
            snackBarSvc.openSnackBar('Failed to read periods', 'error');
        },
    );
    const showMetrics = filter.showMetrics;
    const startDateSelectFilter = startDateFilter().Default(isEditMode ? moment(editReportConfig.report.startDate) : undefined);
    const endDateSelectFilter = endDateFilter().Default(isEditMode ? moment(editReportConfig.report.endDate) : undefined);

    let politicalDateLabel: string;
    if (url === UrlStore.screenNames.politicalRateCard) {
        dateRangeOptionsFilter.hide = true;
        politicalDateLabel = moment(filter.startDate.Value).format('M/D/YYYY') + ' to ' + moment(filter.endDate.Value).format('M/D/YYYY');
    }
    const dialog = matDialog.open(SaveReportPopupComponent, {
        width: '25em',
        data: {
            filters: {
                nameFilter,
                saveMetricSelectionsFilter,
                saveAsGlobalFilter,
                dateRangeOptionsFilter,
                periodSelectFilter,
                quickDateOptionsSelectFilter,
                numberOfPeriodsFilter,
                addAsFavoriteFilter,
                showMetrics,
                startDateSelectFilter,
                endDateSelectFilter,
                politicalDateLabel,
            },
            allFiltersSelectedCheck: filtersCheck,
            isEditMode,
            showSaveMetricSelections: canSaveMetricSelections(editReportConfig),
        },
    });

    dialog.afterClosed().subscribe((filterData) => {
        if (filterData) {
            const data = {} as ReportData;
            const dateRangeFilterSelected = filterData.dateRangeOptionsFilter.Value.name;
            const isRollingThroughQuarterEnd = dateRangeFilterSelected === THROUGH_QUARTER_END_OPTION;
            if (isRollingThroughQuarterEnd) {
                data.rollingThroughQuarterEnd = true;
            }
            data.useRollingDates = dateRangeFilterSelected === 'Save Rolling Dates' || isRollingThroughQuarterEnd;
            data.global = filterData.saveAsGlobalFilter.Value;
            data.favorited = filterData.addAsFavoriteFilter.Value;
            if (isEditMode) {
                data.favorited = editReportConfig.report.isFavorited;
                data.reportName = filterData.nameFilter.value;
            }

            if (keepsReportSettings(editReportConfig, saveMetricSelectionsFilter)) {
                const report = editReportConfig.report;
                data.savedMetrics = report.savedMetrics?.savedMetrics ?? [];
                data.metricsOrder = report.savedMetrics?.savedMetricsOrder ?? [];
                data.savedSegments = report.savedSegments?.savedSegments ?? [];
                data.segmentsOrder = report.savedSegments?.savedSegmentsOrder ?? [];
                data.savedDemos = report.savedDemos?.savedDemos;
                data.savedDemosOrder = report.savedDemos?.savedDemosOrder;
                if (UrlStore.rateCardScreens.includes(url)) {
                    data.stationOrder = report.savedStations?.savedStationOrder;
                    data.marketOrder = report.savedStations?.savedMarketOrder;
                }
            } else {
                const additionalMetricsKey = metricsData?.additionalMetricsKey;
                const additionalMetrics = additionalMetricsKey ? lookupSvc.getFilterFromLocalStorage(additionalMetricsKey) : [];
                data.savedMetrics = additionalMetrics.concat(lookupSvc.getFilterFromLocalStorage(metricsData.metricsKey));
                data.metricsOrder = lookupSvc.getFilterFromLocalStorage(metricsData.metricsOrderKey);
                data.savedSegments = lookupSvc.getFilterFromLocalStorage(segmentsKey);
                data.segmentsOrder = lookupSvc.getFilterFromLocalStorage(segmentsOrderKey);
                data.savedDemos = lookupSvc.getFilterFromLocalStorage(demosKey);
                data.savedDemosOrder = lookupSvc.getFilterFromLocalStorage(deemosOrderKey);
                if (UrlStore.rateCardScreens.includes(url)) {
                    data.stationOrder = lookupSvc.getFilterFromLocalStorage(UrlStore.localStorageKeys.rateCardStationOrder).map(Number);
                    data.marketOrder = lookupSvc.getFilterFromLocalStorage(UrlStore.localStorageKeys.rateCardMarketOrder).map(Number);
                }
            }
            if (data.useRollingDates) {
                if (!isRollingThroughQuarterEnd) {
                    data.rollingPeriod = filterData.periodSelectFilter.Value.name;
                    data.rollingPeriodNumber = Number(filterData.numberOfPeriodsFilter.Value);
                }
                data.startDate = null;
                data.endDate = null;
            } else {
                if (dateRangeFilterSelected === 'Save Fixed Dates') {
                    if (isEditMode) {
                        data.startDate = filterData.startDateSelectFilter.Value.format('YYYY-MM-DD HH:mm:ss');
                        data.endDate = filterData.endDateSelectFilter.Value.format('YYYY-MM-DD HH:mm:ss');
                    } else {
                        data.startDate = filter.startDate?.Value.format('YYYY-MM-DD HH:mm:ss');
                        data.endDate = filter.endDate?.Value.format('YYYY-MM-DD HH:mm:ss');
                    }
                }
                if (dateRangeFilterSelected === 'Quick Date Options') {
                    const periodName = filterData.quickDateOptionsSelectFilter.Value.name;
                    const quarterPeriodId = 3;
                    const yearPeriodId = 4;
                    const isQuarter = periodName.includes('Quarter');
                    const isCurrent = periodName.includes('Current');
                    const qPeriodId = isQuarter ? quarterPeriodId : yearPeriodId;
                    const qPeriod = rateCardPeriods.find((rcPeriod) => rcPeriod?.periodId === qPeriodId);
                    data.startDate = moment(qPeriod.periodStartDate, 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss');
                    if (isCurrent) {
                        data.endDate = moment(qPeriod.periodEndDate, 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss');
                    } else {
                        const unit = isQuarter ? 'quarter' : 'year';
                        data.endDate = moment(qPeriod.periodEndDate, 'YYYY-MM-DD').add(1, unit).format('YYYY-MM-DD HH:mm:ss');
                    }
                }
                if (resetEndDate) {
                    data.endDate = moment(data.endDate, 'YYYY-MM-DD HH:mm:ss').day(0).format('YYYY-MM-DD HH:mm:ss');
                }
                // TODO: Fix this as a part of https://revenueanalytics.atlassian.net/browse/RO-7788
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                if ('startDate' in filterJSON && filterJSON.startDate) {
                    filterJSON.startDate = moment(data.startDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                }
                // @ts-ignore
                if (filterJSON.endDate) {
                    // @ts-ignore
                    filterJSON.endDate = moment(data.endDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                }
            }
            if (!isEditMode && UrlStore.rateCardScreens.includes(url)) {
                // @ts-ignore
                filterJSON.showMetrics = filter.showMetrics;
                // @ts-ignore
                filterJSON.tierValue = filter.tierValue;
                // @ts-ignore
                filterJSON.primaryRateValue = filter.primaryRateValue;
                // @ts-ignore
                filterJSON.alertFilter = filter.alertFilter;
                // @ts-ignore
                filterJSON.quarterlyPCode = filter.quarterlyPCode;
                // @ts-ignore
                filterJSON.pcodeTierValue = filter.pcodeTierValue;
                // @ts-ignore
                filterJSON.showPivoted = filter.showPivoted;
                // @ts-ignore
                filterJSON.quarterlyPoliticalSummary = filter.quarterlyPoliticalSummary;
                // @ts-ignore
                if (filter.totals) {
                    // @ts-ignore
                    filterJSON.totals = filter.totals;
                }
            }
            /* eslint-enable @typescript-eslint/ban-ts-comment */
            callSaveOrUpdateReport(data, filterData, filterJSON, filter, url, tabData, lookupSvc, snackBarSvc, editReportConfig);
        }
    });
};

export const saveReportDashBoard: <T extends FilterTemplate>(
    matDialog: MatDialog,
    url: string,
    filter: T,
    lookupSvc: LookupV2Service,
    snackBarSvc: NotificationService,
    metricsData: { metricsKey: string; metricsOrderKey: string },
    includeQuickDataSelect: boolean,
    includeDateRange: boolean,
    filterJSON: unknown,
    filtersCheck: boolean,
    cablenetCapability?: boolean,
    tabData?: number,
    editReportConfig?: EditReportConfig,
) => void = (
    matDialog: MatDialog,
    url: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBarSvc: NotificationService,
    metricsData: { metricsKey: string; metricsOrderKey: string },
    includeQuickDataSelect: boolean,
    includeDateRange: boolean,
    filterJSON: unknown,
    filtersCheck: boolean,
    cablenetCapability: boolean,
    tabData: number = 1,
    editReportConfig?: EditReportConfig,
) => {
    const isEditMode = editReportConfig?.isEditMode || false;
    let existingReports: SavedReport[] = [];

    lookupSvc.loadSavedReports(url, tabData).subscribe(
        (result) => {
            existingReports = result;
        },
        (error) => {
            console.error(error);
        },
    );
    const originalName = isEditMode ? editReportConfig.report.reportName : '';
    // @ts-expect-error TS7053: implicit any
    const nameFilter = new StringFilter('Report Name')
        .Validator(
            (val: string): boolean => {
                if (val === null) {
                    return false;
                }
                const upperVal = val.toUpperCase();
                if (isEditMode && upperVal === originalName.toUpperCase()) {
                    return true;
                }
                return existingReports.every((report) => report.reportName.toUpperCase() !== upperVal);
            },
            // @ts-expect-error TS7024: implicit any return type
            () => `Name Already Exists ${nameFilter.value}`,
        )
        .Required(true)
        .Default(originalName);

    const saveMetricSelectionsFilter = saveMetricSelectionsToggle(isEditMode);

    const saveAsGlobalFilter = new BooleanFilter(
        isEditMode ? 'Global' : 'Save as Global',
        isEditMode ? editReportConfig.report.isGlobal : false,
    )
        .Slider(true)
        .Toggle(!filter.config.roleCapability.rate_optics.general.create_global_report)
        .Hint('Make this report available to all users');

    if (isEditMode && !editReportConfig.report.isReportOwner) {
        saveAsGlobalFilter.Readonly();
    }

    const addAsFavoriteFilter = new BooleanFilter('Set as a Favorite', false).Slider(true);

    const numberOfPeriodsFilter = new NumberFilter('Number of Periods').Min(1).Required(true);

    const periodSelectFilter = new SelectFilter<Period>('Period').OnChange((period: Period) => {
        const periodName = period?.name ? period.name : 'Period';
        numberOfPeriodsFilter.Key = `Number of ${periodName}s`;
        startingPeriodFilter.Key = `Starting ${periodName}`;
    });
    const allowedPeriods = cablenetCapability ? ['Quarter'] : ['Month', 'Quarter'];

    const startingOptions = ['Current', 'Previous'];
    const startingPeriodFilter = new SelectFilter<string>('Starting Period').Options(startingOptions).Required(true);

    lookupSvc.getPeriods(url).subscribe(
        (res) => {
            periodSelectFilter.Options(res);
            periodSelectFilter.Options(periodSelectFilter.options.filter((option) => allowedPeriods.includes(option.name)));

            if (isEditMode && editReportConfig.report.isRollingDates) {
                const periodIndex = res.findIndex((p) => p.name === editReportConfig.report.filter.rolling_period);
                if (periodIndex !== -1) {
                    periodSelectFilter.Value = res[periodIndex];
                    startingPeriodFilter.Value =
                        parseInt(editReportConfig.report.filter.rolling_period_start, 10) === 0 ? startingOptions[0] : startingOptions[1];
                    numberOfPeriodsFilter.Value = editReportConfig.report.filter.rolling_period_number;
                }
            }
        },
        (error) => {
            console.error(error);
        },
    );

    let dateRangeOptionsFilter: RadioFilter<FilterOption> = null;
    if (includeDateRange) {
        const radioOptions = [];

        radioOptions.push({
            name: 'Save Fixed Dates',
            filterFunc: () => true,
        });
        radioOptions.push({
            name: 'Save Rolling Dates',
            filterFunc: () => true,
        });
        dateRangeOptionsFilter = new RadioFilter<FilterOption>('').DisplayKey(false).Options(radioOptions);
        const dataRangeDefaultValue = isEditMode && editReportConfig.report.isRollingDates ? 1 : 0;
        dateRangeOptionsFilter.Value = dateRangeOptionsFilter.options[dataRangeDefaultValue];
    }

    const dialog = matDialog.open(SaveReportPopupComponent, {
        data: {
            filters: {
                nameFilter,
                saveMetricSelectionsFilter,
                saveAsGlobalFilter,
                addAsFavoriteFilter,
                dateRangeOptionsFilter,
                periodSelectFilter,
                startingPeriodFilter,
                numberOfPeriodsFilter,
            },
            allFiltersSelectedCheck: filtersCheck,
            isEditMode,
            showSaveMetricSelections: canSaveMetricSelections(editReportConfig),
        },
        width: '25em',
    });

    dialog.afterClosed().subscribe((filterData) => {
        if (filterData) {
            const data = {} as ReportData;
            if (keepsReportSettings(editReportConfig, saveMetricSelectionsFilter)) {
                const report = editReportConfig.report;
                data.savedMetrics = report.savedMetrics?.savedMetrics ?? [];
                data.metricsOrder = report.savedMetrics?.savedMetricsOrder ?? [];
            } else if (filterData.saveMetricSelectionsFilter.Value) {
                data.savedMetrics = lookupSvc.getFilterFromLocalStorage(metricsData.metricsKey);
                data.metricsOrder = lookupSvc.getFilterFromLocalStorage(metricsData.metricsOrderKey);
            }
            const dateRangeFilterSelected = filterData.dateRangeOptionsFilter?.Value?.name;
            data.useRollingDates = dateRangeFilterSelected === 'Save Rolling Dates';
            data.global = filterData.saveAsGlobalFilter.Value;
            data.favorited = filterData.addAsFavoriteFilter.Value;

            if (isEditMode) {
                data.favorited = editReportConfig.report.isFavorited;
                data.reportName = filterData.nameFilter.value;
            }

            if (dateRangeFilterSelected === 'Save Rolling Dates') {
                data.rollingPeriod = filterData.periodSelectFilter.Value.name;
                data.rollingPeriodStart = filterData.startingPeriodFilter.Value === 'Current' ? 0 : -1;
                data.rollingPeriodNumber = Number(filterData.numberOfPeriodsFilter.Value);
            }
            callSaveOrUpdateReport(data, filterData, filterJSON, filter, url, tabData, lookupSvc, snackBarSvc, editReportConfig);
        }
    });
};

const callSaveOrUpdateReport: (
    data: ReportData,
    filterData: {
        nameFilter: StringFilter;
        saveAsGlobalFilter: BooleanFilter;
        dateRangeOptionsFilter?: RadioFilter<FilterOption>;
        periodSelectFilter?: SelectFilter<LookupItem>;
        quickDateOptionsSelectFilter?: SelectFilter<LookupItem>;
        startingPeriodFilter?: SelectFilter<string>;
        numberOfPeriodsFilter?: NumberFilter;
        addAsFavoriteFilter: BooleanFilter;
        showMetrics?: boolean;
    },
    filterJSON: unknown,
    filter: FilterTemplate,
    url: string,
    tab: number,
    lookupSvc: LookupV2Service,
    snackBarSvc: NotificationService,
    editReportConfig?: EditReportConfig,
) => void = (data, filterData, filterJSON, filter, url, tab, lookupSvc, snackBarSvc, editReportConfig) => {
    let selectedChannels: LookupItem[];
    const isEditMode = editReportConfig && editReportConfig.isEditMode;
    let reportId = null;
    if (isEditMode) {
        // @ts-expect-error TS7053: implicit any
        selectedChannels = filterJSON.channels;
        reportId = editReportConfig.report.reportId;
    } else {
        // @ts-expect-error TS7053: implicit any
        const channelsSelected = filter.channels?.value || [filter.channelsSingleSelect.Value.id];
        if (channelsSelected.length > 0 && isNaN(channelsSelected[0])) {
            selectedChannels = channelsSelected.map((channel: { id: number }) => channel.id);
        } else {
            selectedChannels = channelsSelected;
        }
    }
    const stringChannels: string = selectedChannels ? selectedChannels.join(',') : '';

    const isNewReport = isEditMode ? false : true;
    // @ts-expect-error TS7053: implicit any
    const reportName = filterData.nameFilter.value;

    lookupSvc.saveReport(url, tab, data, filterJSON, stringChannels, reportName, isNewReport, reportId).subscribe(
        (reportIdSaved: number) => {
            if (isEditMode) {
                lookupSvc.loadSavedReports(url, tab).subscribe((reports) => {
                    const originalArray = editReportConfig.loadReportDialogObj.savedReports;
                    originalArray.length = 0;
                    originalArray.push(...reports);
                    editReportConfig.loadReportDialogObj.categorizeReports();
                    snackBarSvc.openSnackBar('Updated report successfully', 'success');
                });
            } else {
                lookupSvc.getSavedReport(reportIdSaved).subscribe((savedReports: SavedReport[]) => {
                    filter.lastLoadedSavedReport = savedReports[0];
                });
                snackBarSvc.openSnackBar('Saved report successfully', 'success');
            }
        },
        (error: string) => {
            const type = isNewReport ? 'save' : 'update';
            console.error(error);
            snackBarSvc.openSnackBar('Failed to ' + type + ' report', 'error');
        },
    );
};

export const editExitSavedReport = <T>(
    matDialog: MatDialog,
    url: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBarSvc: NotificationService,
    filterJSON: T,
    tab: number = 1,
    isEdit: boolean,
    metricsData: MetricsData,
): void => {
    const reportId = filter.lastLoadedSavedReport.reportId;
    const reportName = filter.lastLoadedSavedReport.reportName;
    const dialogText = isEdit
        ? `Update report '${reportName}'\nwith your current filter selections?\nThis will overwrite the existing version.`
        : `Would you like to override the report '${reportName}'\nwith the current filter settings?`;
    const dialog = matDialog.open(ConfirmDialogComponent, {
        data: {
            text: dialogText,
            autoConfirm: false,
            noClickClose: true,
            confirmText: isEdit ? 'Confirm' : 'Yes',
            denyText: isEdit ? 'Cancel' : 'No',
            denyClose: false,
            showCancel: !isEdit,
            cancelText: isEdit ? '' : 'Cancel',
        },
    });
    dialog.afterClosed().subscribe((result) => {
        if (result === CONFIRM_DIALOG) {
            const data = {} as ReportData;
            const additionalMetricsKey = metricsData?.additionalMetricsKey;
            const additionalMetrics = additionalMetricsKey ? lookupSvc.getFilterFromLocalStorage(additionalMetricsKey) : [];
            data.savedMetrics = additionalMetrics.concat(lookupSvc.getFilterFromLocalStorage(metricsData.metricsKey));
            data.metricsOrder = lookupSvc.getFilterFromLocalStorage(metricsData.metricsOrderKey);
            // @ts-expect-error TS7053: implicit any
            filterJSON.alertFilter = filter.alertFilter;
            // @ts-expect-error TS7053: implicit any
            filterJSON.tierValue = filter.tierValue;
            // @ts-expect-error TS7053: implicit any
            filterJSON.pcodeTierValue = filter.pcodeTierValue;
            // @ts-expect-error TS7053: implicit any
            if (filter.totals) {
                // @ts-expect-error TS7053: implicit any
                filterJSON.totals = filter.totals;
            }
            // @ts-expect-error TS7053: implicit any
            filterJSON.showMetrics = filter.showMetrics;
            lookupSvc.saveReport(url, tab, data, filterJSON, null, reportName, false, reportId, true).subscribe(
                () => {
                    snackBarSvc.openSnackBar('Updated report successfully', 'success');
                },
                (error: string) => {
                    snackBarSvc.openSnackBar('Failed to update report', 'error');
                },
            );
        } else {
            if (!isEdit && result === DENY_DIALOG) {
                filter.lastLoadedSavedReport = null;
            }
        }
    });
};

export const loadReport: (
    matDialog: MatDialog,
    url: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    tabData?: number,
    tabId?: string,
    demosKey?: string,
    demosOrderKey?: string,
) => void = (
    matDialog: MatDialog,
    url: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    tabData: number = 1,
    tabId?: string,
    demosKey?: string,
    demosOrderKey?: string,
) => {
    lookupSvc.loadSavedReports(url, tabData).subscribe((reports) => {
        const dialog = matDialog.open(LoadReportPopupComponent, {
            data: {
                url,
                filter,
                reports,
                ...(url === UrlStore.screenNames.rateCard && { tab: tabData }),
                ...(tabId && { tabId }),
            },
        });
        dialog.afterClosed().subscribe((savedFilter) => {
            filter.lastLoadedSavedReport = savedFilter.selectedReport;
            loadReportIntoFilters(
                savedFilter,
                filter,
                lookupSvc,
                metricsKey,
                metricsOrderKey,
                segmentsKey,
                segmentsOrderKey,
                afterLoad,
                demosKey,
                demosOrderKey,
            );
        });
    });
};

const loadReportIntoFilters: (
    savedFilter: SavedReportInfoFromPopUp,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => void = (
    savedFilter: SavedReportInfoFromPopUp,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey: string = '',
    demosOrderKey: string = '',
) => {
    if (savedFilter?.filter) {
        filter.savedReportLoaded = true;
        lookupSvc.saveFilterToLocalStorage(metricsKey, savedFilter.filter.savedMetrics);
        lookupSvc.saveFilterToLocalStorage(metricsOrderKey, savedFilter.filter.savedMetricsOrder);
        lookupSvc.saveFilterToLocalStorage(segmentsKey, savedFilter.filter.savedSegments);
        lookupSvc.saveFilterToLocalStorage(segmentsOrderKey, savedFilter.filter.savedSegmentsOrder);
        lookupSvc.saveFilterToLocalStorage(demosKey, savedFilter.filter.savedDemos);
        lookupSvc.saveFilterToLocalStorage(demosOrderKey, savedFilter.filter.savedDemosOrder);

        if (filter instanceof AdvertiserInsightsFilters) {
            loadSavedValuesIntoFiltersAsync(filter, savedFilter.filter).subscribe((response) => {
                const cascadingFilterData = filter.getCascadingFilterData(filter.advertisers.name, filter.advertisers.Value);
                filter.advertiserInsightsSvc.getCascadingFilters(cascadingFilterData).subscribe(
                    (data: AdvertiserInsightsDependentFiltersResponse) => {
                        const throwError = filter.shouldThrowNoDataWarning(data);
                        if (throwError) {
                            filter.noDataAvailableWarning();
                        }
                        // @ts-expect-error TS7053: implicit any
                        filter.updateSavedReportCascadingFilters(data, savedFilter.filter);
                        filter.ordersFilters =
                            (savedFilter.filter as { ordersFilters?: Record<string, unknown> | null })?.ordersFilters ?? null;
                        filter.savedReportLoaded = false;
                        afterLoad();
                    },
                    (error) => {
                        filter.failedToLoadFilters(filter.advertisers.name, error);
                    },
                );
            });
        } else {
            if ('channelGroup' in savedFilter.filter) {
                // TODO: Fix this as a part of https://revenueanalytics.atlassian.net/browse/RO-7788
                /* eslint-disable @typescript-eslint/ban-ts-comment */
                (filter.channelGroup as SelectFilter<ChannelGroupItem>)?.Default(
                    filter.channelGroup.options.find(
                        // @ts-ignore
                        (i) => i.id === savedFilter.filter.channelGroup,
                    ),
                );
                // @ts-ignore
            } else if (filter.channels?.options.length > 0) {
                // If filters already have channels loaded
                if ('channels' in savedFilter.filter && savedFilter.filter.channels === 'ALL') {
                    // Default to channel group from first channel in dropdown
                    // @ts-ignore
                    const channelGroup = filter.channels.options[0].channelGroup;
                    // @ts-ignore
                    filter.channelGroup?.Default(filter.channelGroup.options.find((i) => i.id === channelGroup));
                } else if (
                    'channels' in savedFilter.filter &&
                    Array.isArray(savedFilter.filter.channels) &&
                    savedFilter.filter.channels.length > 0
                ) {
                    // Find first channel in saved report, map it to pre existing filter, and use that channel group
                    const firstChannel = savedFilter.filter.channels[0];
                    // @ts-ignore
                    const firstChannelGroup = filter.channels.options.find((i) => i.id === firstChannel).channelGroup;
                    // @ts-ignore
                    filter.channelGroup?.Default(filter.channelGroup.options.find((i) => i.id === firstChannelGroup));
                }
            } else {
                // Last resource, default to first

                (filter.channelGroup as SelectFilter<ChannelGroupItem>)?.Default(filter.channelGroup?.options[0]);
            }
            if ('showMetrics' in savedFilter.filter) {
                filter.showMetrics = savedFilter.filter.showMetrics as boolean;
            }
            if (isRateCardFilter(filter)) {
                // @ts-ignore
                filter.quarterlyPCode = savedFilter.filter.quarterlyPCode;
                // @ts-ignore
                filter.showPivoted = savedFilter.filter.showPivoted;
                // @ts-ignore
                filter.quarterlyPoliticalSummary = savedFilter.filter.quarterlyPoliticalSummary;
                // @ts-ignore
                filter.totals = savedFilter.filter.totals;
                // @ts-ignore
                filter.primaryRateValue = savedFilter.filter.primaryRateValue;
                // @ts-ignore
                filter.alertFilter = savedFilter.filter.alertFilter || filter.alertFilter;
                // @ts-ignore
                filter.tierValue = savedFilter.filter.tierValue || filter.tierValue;
                // @ts-ignore
                filter.pcodeTierValue = savedFilter.filter.pcodeTierValue || filter.pcodeTierValue;
            }
            filter.changeDates = false;
            loadSavedValuesIntoFiltersAsync(filter, savedFilter.filter).subscribe((_) => {
                if (filter?.dynamicFilters) {
                    // to fix dynamic properties with select all not being passed to load data api.
                    // this is happening due to cascading filter values to child filters.
                    /* eslint-disable @typescript-eslint/no-explicit-any */
                    const dynamicProperties:
                        | { finishLoad: (arg0: any) => void; setValueNoOnChange: (arg0: any) => void }[]
                        | (StringFilter | TimeFilter | MultiSelectFilter<SegmentItem> | SelectFilter<SegmentItem>)[] = [];
                    /* eslint-enable @typescript-eslint/no-explicit-any */
                    Object.keys(filter?.dynamicFilters).map((key) => {
                        // @ts-expect-error TS7053: implicit any
                        if (savedFilter.filter[key] === 'ALL') {
                            // @ts-expect-error TS7053: implicit any
                            dynamicProperties.push(filter.dynamicFilters[key]);
                        }
                    });
                    forkJoin(
                        // @ts-expect-error TS7053: implicit any
                        [forkJoin(dynamicProperties.map((prop) => prop.Load())).pipe(defaultIfEmpty([]))],
                    ).subscribe(([dynamicPropertiesValueArray]) => {
                        dynamicPropertiesValueArray.forEach((value, index) => {
                            // @ts-expect-error TS7053: implicit any
                            dynamicProperties[index].finishLoad(value);
                            dynamicProperties[index].setValueNoOnChange(value);
                        });
                        afterLoad();
                    });
                } else {
                    afterLoad();
                }
            });
        }
        /* eslint-enable @typescript-eslint/ban-ts-comment */
    }
};

const isRateCardFilter = (
    filter: FilterTemplate,
): filter is DynamicRateCardFiltersV2 | PoliticalRateCardFiltersV2 | DigitalRateCardFilters =>
    filter instanceof DynamicRateCardFiltersV2 || filter instanceof PoliticalRateCardFiltersV2 || filter instanceof DigitalRateCardFilters;

// Shared by loadSavedReport/loadSharedReport/loadDefaultReport: unpacks a fetched
// SavedReport's metric/segment selections into its filter blob and applies
// it. markAsLoaded controls whether the "Reports" button shows this as a loaded
// saved report (skipped for the silent default-report auto-load).
const applySavedReportToFilters = (
    savedReport: SavedReport,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
    markAsLoaded = true,
) => {
    if (markAsLoaded) {
        filter.lastLoadedSavedReport = savedReport;
    }
    const savedFilter = savedReport.filter as SavedReportSomething;
    savedFilter.savedMetrics = savedReport.savedMetrics.savedMetrics;
    savedFilter.savedMetricsOrder = savedReport.savedMetrics.savedMetricsOrder;
    savedFilter.savedSegments = savedReport.savedSegments.savedSegments;
    savedFilter.savedSegmentsOrder = savedReport.savedSegments.savedSegmentsOrder;
    loadReportIntoFilters(
        { filter: savedFilter, selectedReport: savedReport },
        filter,
        lookupSvc,
        metricsKey,
        metricsOrderKey,
        segmentsKey,
        segmentsOrderKey,
        afterLoad.bind(this),
        demosKey,
        demosOrderKey,
    );
};

export const loadSavedReport: (
    reportId: number,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBar: NotificationService,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => void = (
    reportId: number,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBar: NotificationService,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => {
    lookupSvc.getSavedReport(reportId).subscribe((reports) => {
        const savedReport = reports[0];
        if (savedReport && savedReport.filter) {
            if ('channels' in savedReport && savedReport.channels.length === 0) {
                snackBar.openSnackBar(
                    `You do not presently have access to data for the ${filter.config.customerConfig.station || 'station'}(s) contained in this report. Please contact your RateOptics Admin to request access.`,
                    'error',
                );
            } else {
                applySavedReportToFilters(
                    savedReport,
                    filter,
                    lookupSvc,
                    metricsKey,
                    metricsOrderKey,
                    segmentsKey,
                    segmentsOrderKey,
                    afterLoad,
                    demosKey,
                    demosOrderKey,
                );
            }
        } else {
            snackBar.openSnackBar('No Access to the report', 'error');
        }
    });
};

export const loadSharedReport: (
    sharedReportId: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBar: NotificationService,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => void = (
    sharedReportId: string,
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    snackBar: NotificationService,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => {
    lookupSvc.getSharedReport(sharedReportId).subscribe(
        (reports) => {
            const savedReport = reports[0];
            if (savedReport && savedReport.filter) {
                if ('channels' in savedReport && savedReport.channels.length === 0) {
                    snackBar.openSnackBar(
                        `You do not presently have access to data for the ${filter.config.customerConfig.station || 'station'}(s) contained in this report. Please contact your RateOptics Admin to request access.`,
                        'error',
                    );
                } else {
                    applySavedReportToFilters(
                        savedReport,
                        filter,
                        lookupSvc,
                        metricsKey,
                        metricsOrderKey,
                        segmentsKey,
                        segmentsOrderKey,
                        afterLoad,
                        demosKey,
                        demosOrderKey,
                    );
                }
            } else {
                snackBar.openSnackBar('No Access to the report', 'error');
            }
        },
        (error) => {
            snackBar.openSnackBar('Invalid report id', 'error');
        },
    );
};

// Loads the current user's assigned default report as the initial filter state
// (never marking it as "loaded", so the Reports button shows no loaded report
// name), falling back to onNoDefaultReport when none is assigned or it's no
// longer eligible.
export const loadDefaultReport: (
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    onNoDefaultReport: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => void = (
    filter: FilterTemplate,
    lookupSvc: LookupV2Service,
    metricsKey: string,
    metricsOrderKey: string,
    segmentsKey: string,
    segmentsOrderKey: string,
    afterLoad: () => void,
    onNoDefaultReport: () => void,
    demosKey?: string,
    demosOrderKey?: string,
) => {
    lookupSvc.getDefaultReportForRateCard().subscribe({
        next: (resolvedReport) => {
            if (!resolvedReport?.filter) {
                onNoDefaultReport();
                return;
            }
            applySavedReportToFilters(
                resolvedReport,
                filter,
                lookupSvc,
                metricsKey,
                metricsOrderKey,
                segmentsKey,
                segmentsOrderKey,
                afterLoad,
                demosKey,
                demosOrderKey,
                false,
            );
        },
        error: () => onNoDefaultReport(),
    });
};
